import { computed, onBeforeUnmount, onMounted, ref, Ref } from 'vue'
import throttle from 'lodash/throttle'
import { SECOND } from '@shared/constants/time'
import {requestAnimationFrame } from '@remote/utils/base'
/**
 * 用来统一处理分页加载中的请求token
 * 约定：
 * 1. 初始请求token为 ''
 * 2. 使用上一个请求返回的token作为请求下一页的token
 * 2. 请求到末尾，没有后续数据了，接口返回token为 ''
 */
export const useGetListWithToken = <T>(requestFunction: (token: string) => Promise<({token: string; data: T[]})>) => {
  const list = ref<T[]>([])
  let currentToken = ''
  const isEnd = ref(false)
  /**
   * 标记位，是否正在刷新中，防止重入。
   */
  let isRefreshing = false

  const commonRequestAction = async () => {
    const result = await requestFunction(currentToken)
    if (result.token === '') {
      isEnd.value = true
    }
    currentToken = result.token
    return result.data
  }

  const load = async () => {
    if (isEnd.value) return
    if (isRefreshing) return
    const data = await commonRequestAction()
    if (data) {
      const rs = ref(data)
      if (!isRefreshing) { // 优先刷新
        list.value = [...list.value, ...rs.value]
      }
    }
  }

  const refresh = async () => {
    isEnd.value = false
    isRefreshing = true
    currentToken = ''
    const data = await commonRequestAction()
    if (data) {
      const rs = ref(data)
      list.value = [...rs.value]
      isRefreshing = false
    }
  }

  return {
    isEnd,
    list,
    load,
    refresh,
  }
}

const enum FEEDBACK_ERROR_TYPE {
  ERR_UNKNOW = 'ERR_UNKNOW',
  ERR_NAME = 'ERR_NAME',
  ERR_YEAR = 'ERR_YEAR',
  ERR_POSTER = 'ERR_POSTER',
}

export const useCinemaStoreCommon = (
  { API, targetRef, hookAfterScrape }: { API: API_NFO.APISet; targetRef: Ref<string>; hookAfterScrape?: (from: string) => void },
) => {

  /**
   * 是否正在刮削中
   */
  const isScraping = ref(false)
  /**
   * 最后一次刮削的时间戳
   */
  const lastScrapeTimestamp = ref(0)
  /**
   * 获取最近观看列表
   */
  const getRecentlyWatchNFOList = async (token = '') => {
    const result = await API.getNFOList({
      last_page_token: token,
      page_size: 30,
      order_by: 'last_play_time',
      // order: 'desc',
    })
    return {
      token: result.page_token ?? '',
      data: result.nfos ?? [],
    }
  }
  /**
   * 柯里化获取刮削列表
   */
  const curryGetNFOList = async (classify: API_NFO.NFOClass, token: string) => {
    const result = await API.getNFOList({
      class: classify,
      last_page_token: token,
      page_size: 30,
    })
    return {
      token: result.page_token ?? '',
      data: result.nfos ?? [],
    }
  }
  /**
   * 柯里化获取电影刮削列表
   */
  const curryGetMovieNFOList = (token: string) => curryGetNFOList('movie', token)
  /**
   * 柯里化获取电视剧刮削列表
   */
  const curryGetTvNFOList = (token: string) => curryGetNFOList('tv', token)
  /**
   * 柯里化获取其他刮削列表
   */
  const curryGetOtherNFOList = async (token: string) => {
    const result = await API.getOtherNFOList({
      last_page_token: token,
      page_size: 30,
    })
    console.log('curryGetOtherNFOList', result)
    return {
      token: result.page_token ?? '',
      data: result.nfos ?? [],
    }
  }

  /**
   * 电影、电视剧、其他 的列表、加载函数、刷新函数、是否到底
   */
  const { isEnd: isMovieListEnd, list: movieList, load: loadMovieNFOList, refresh: refreshMovieNFOList } = useGetListWithToken(curryGetMovieNFOList)
  const { isEnd: isTvListEnd, list: tvList, load: loadTvNFOList, refresh: refreshTvNFOList } = useGetListWithToken(curryGetTvNFOList)
  const { isEnd: isOtherListEnd, list: otherList, load: loadOtherNFOList, refresh: refreshOtherNFOList } = useGetListWithToken(curryGetOtherNFOList)
  const { isEnd: isRecentListEnd, list: recentList, load: loadRecentNFOList, refresh: refreshRecentNFOList } = useGetListWithToken(getRecentlyWatchNFOList)

  /**
   * 刷新所有列表
   */
  const refreshAllList = () => {
    const requestIdleCallback = window.requestIdleCallback || requestAnimationFrame
    requestIdleCallback(refreshMovieNFOList)
    requestIdleCallback(refreshTvNFOList)
    requestIdleCallback(refreshRecentNFOList)
    requestIdleCallback(refreshOtherNFOList)
  }

  /**
   * 轮询刮削任务状态
   */
  const pollScrapeTaskStatus = async (taskID?: string, from = '', time = 1) => {
    const result = await (API.getScrapeTaskStatus(targetRef.value, taskID).catch((err) => {
      if (err.status === 404) {
        console.log('Task can not be queried yet.Retry in 2 second.')
      }
      setTimeout(() => {
        pollScrapeTaskStatus(taskID, from, time > 5 ? 0 : time + 1)
      }, 2000)
      return
    }))
    if (!result) return
    if (['PHASE_TYPE_RUNNING', 'PHASE_TYPE_PENDING'].includes(result?.phase ?? '')) {
      setTimeout(() => {
        if (time === 5) {
          // 每轮询5次刷新一次列表，即10s一次。但是实际还得算上请求的时间和中间代码执行的时间，实际不止10s一次。
          refreshAllList()
        }
        pollScrapeTaskStatus(taskID, from, time >= 5 ? 1 : time + 1)
      }, 2000)
      return
    } else if (result?.phase === 'PHASE_TYPE_ERROR') {
      // message({
      //   type: 'error',
      //   message: result?.message, 
      // })
    }
    refreshAllList()
    isScraping.value = false
    lastScrapeTimestamp.value = Date.now()
    if (hookAfterScrape) {
      hookAfterScrape(from)
    }

  }

  /**
   * 创建刮削任务，设置刮削中标记位并开始轮询
   */
  const createScrapeTask = async (from = '') => {
    console.log('createScrapeTask 1')
    if (!isScraping.value) {
      isScraping.value = true
      refreshAllList()
      setTimeout(() => {
        isScraping.value = false
      }, 3 * 1000)
      // 不再查询设备状态
      // 先看看当前有没有正在执行的刮削任务（PENDING / RUNNING）
      // const runningTasks = await API.getRunningScrapeTaskList(targetRef.value)
      // console.log('runningTasks', runningTasks)
      // if (runningTasks?.length > 0) {
      //   // 有的话，轮询当前执行中的刮削任务
      //   pollScrapeTaskStatus(runningTasks[0].id, from)
      // } else {
      //   // 没有才创建刮削任务
      //   const result = await API.createScrapeTask('run', targetRef.value)
      //   pollScrapeTaskStatus(result.id, from)
      // }
    }
  }

  /**
   * 刷新刮削目录
   * - 如果没有进行中的任务，那么创建一个刮削任务
   * - 如果有进行中的任务，那么轮询这个任务的刮削状态
   */
  const refreshScrapeTaskStatus = async () => {
    try {
      const result = await API.getScrapeTaskStatus(targetRef.value)
      if (result) { // TODO: 条件不一定准确，需要确认
        // 如果有正在进行的刮削任务
        pollScrapeTaskStatus()
        isScraping.value = true
      } else {
        createScrapeTask()
      }
    } catch (error) {
      console.error(error)
    }
  }



  const submitFeedback = async (item: API_NFO.NFO, errTypes: string[], content: string, uid:string) => {
    return API.submitFeedback({
      content,
      error_types: errTypes,
      file_name: item.video_info?.file_name ?? '',
      real_path: item.video_info?.real_path ?? '',
      id: item.scrape_result?.id ?? 0,
      name_feedback: errTypes.includes(FEEDBACK_ERROR_TYPE.ERR_NAME) ? content : '',
      year_feedback: errTypes.includes(FEEDBACK_ERROR_TYPE.ERR_YEAR) ? content : '',
      poster_feedback: errTypes.includes(FEEDBACK_ERROR_TYPE.ERR_POSTER) ? content : '',
      uid,
    })
  }


  return {
    isScraping,
    lastScrapeTimestamp,
    movieList,
    isMovieListEnd,
    loadMovieNFOList,
    refreshMovieNFOList,
    tvList,
    isTvListEnd,
    loadTvNFOList,
    refreshTvNFOList,
    otherList,
    isOtherListEnd,
    loadOtherNFOList,
    refreshOtherNFOList,
    recentList,
    isRecentListEnd,
    loadRecentNFOList,
    refreshRecentNFOList,
    createScrapeTask,
    refreshAllList,
    refreshScrapeTaskStatus,
    submitFeedback,
  }

}

export const useSourceSetting = (API: API_NFO.APISet) => {
  interface DirWithLocalState extends API_NFO.Dir {
    isChecked: boolean
  }
  /**
   * 路径列表，包含是否选中信息
   */
  const list = ref<DirWithLocalState[]>([])

  /**
   * 是否为初始化
   */
  const hasInit = ref(true)

  /**
   * 是否正在获取路径设置
   */
  const isLoading = ref(true)

  /**
 * 关闭弹窗时是否需要触发刮削
 * 如果列表没有发生过改动，那就不需要重新刮削
 */
  let needScrapeWhenClose = false

  const isAllChecked = computed(() => {
    return list.value.every(i => i.isChecked)
  })

  const isAllUnchecked = computed(() => {
    return list.value.every(i => !i.isChecked)
  })

  /**
 * 全选 -> 全不选
 * 部分选择 -> 全选
 * 全不选 -> 全选
 */
  const toggleCheckAll = () => {
    // isChecked 这个变量的下一个状态
    const willIsCheckedBe = !isAllChecked.value
    list.value = list.value.map(i => {
      return {
        ...i,
        isChecked: willIsCheckedBe,
      }
    })
  }

  const deleteItem = (index: number) => {
    list.value.splice(index, 1)
    // 列表删除项后，需要重新刮削
    needScrapeWhenClose = true
  }

  const addLocalStateIntoDirs = ({ dirs, isChecked }: { dirs: API_NFO.Dir[]; isChecked: boolean}): DirWithLocalState[] => {
    return dirs.map((i) => {
      return {
        ...i,
        isChecked,
      }
    })
  }

  const setSourceDir = async (scrapeFunction: () => void, afterSetDir: () => void) => {
    try {
      const dirs = list.value.filter(i => i.isChecked).filter(i => Boolean(i.real_path)).map(i => i.real_path!)
      const result = await API.setNFODirs({ dirs })
      if (result.message !== 'ok') {
        throw new Error(result.message)
      }
      if (needScrapeWhenClose) {
        scrapeFunction()
      }
      afterSetDir()
    } catch (error) {
      throw new Error(error.error ?? error.message ?? '设置刮削目录失败')
    }
  }
  
  const addDir = (path: string) => {
    list.value.push({
      isChecked: true,
      real_path: path,
    })
    // 列表增加项后，需要重新刮削
    needScrapeWhenClose = true
  }

  onMounted(async () => {
  // preset: false 是获取无预设的列表，如果为空则说明未设置过刮削目录
    const result = await API.getNFODirs({ preset: false }) 
    // 是否设置过刮削目录
    hasInit.value = Array.isArray(result.dirs) && result.dirs.length > 0 
    if (hasInit.value) {
      list.value = addLocalStateIntoDirs({ dirs: result.dirs ?? [], isChecked: true })
      isLoading.value = false
    } else {
      const presets = await API.getNFODirs({ preset: true })
      list.value = addLocalStateIntoDirs({ dirs: presets.dirs ?? [], isChecked: true })
      isLoading.value = false
      // 初始化设置路径，弹窗关闭时需要刮削
      needScrapeWhenClose = true
    }
  })

  return {
    list,
    hasInit,
    isLoading,
    isAllChecked,
    isAllUnchecked,
    addDir,
    deleteItem,
    toggleCheckAll,
    setSourceDir,
  }

}

export const useCinemaFeedback = (nfo: Ref<API_NFO.NFO>) => {
  const problemTypes = ref([
    {
      id: FEEDBACK_ERROR_TYPE.ERR_NAME,
      text: '显示的名称错误',
    },
    {
      id: FEEDBACK_ERROR_TYPE.ERR_YEAR,
      text: '显示的年份错误',
    },
    {
      id: FEEDBACK_ERROR_TYPE.ERR_POSTER,
      text: '显示的海报错误',
    },
  ])

  const additionRef = ref('')

  /**
 * 展示影片信息来源
 */
  const infoOrigin = computed(() => {
    const infoOriginsArray: string[] = []
    if (String(nfo.value?.scrape_result?.tmdb_id)) {
    // 将分类映射到tmdb的路径
      const classMap: { [key in Exclude<API_NFO.NFOClass, 'other'>]: string } = {
        tv: 'tv',
        movie: 'movie',
      }
      const categoryPath = classMap[nfo.value?.scrape_result?.class ?? 'other']
      if (categoryPath) {
        infoOriginsArray.push(`https://www.themoviedb.org/${categoryPath}/${nfo.value?.scrape_result?.tmdb_id}`)
      }
    }
    if (String(nfo.value?.scrape_result?.imdb_id)) {
      infoOriginsArray.push(`https://www.imdb.com/title/${nfo.value?.scrape_result?.imdb_id}`)
    }
    return infoOriginsArray.length > 0 ? infoOriginsArray.join('； ') + '；' : ''
  })

  const userUploadInfo = computed(() => {
    if (nfo.value.scrape_result?.image_upload_info?.upload_time) {
      const name = nfo.value.scrape_result?.image_upload_info.nickname
      const uploadTime = new Date(Number(nfo.value.scrape_result?.image_upload_info.upload_time) * SECOND).toLocaleDateString()
      if (infoOrigin.value) {
        return `用户<strong>${name}</strong>上传（${uploadTime}）；`
      } else {
        // 只有用户上传信息时，附加 公开网络搜索
        return `用户<strong>${name}</strong>上传（${uploadTime}）；公开网络搜索；`
      }
    }
    return ''
  })

  const currentProblems = ref<string[]>([])

  const isProblemChecked = (problemID: string) => {
    return currentProblems.value.includes(problemID)
  }

  /**
   * 问题类型和补充说明，二者有一个不为空即可提交；
   * 反之即为不可提交。
   */
  const isSubmitDisabled = computed(() => {
    return !(currentProblems.value.length > 0 || additionRef.value.length > 0)
  })

  const checkProblem = (problemID: string) => {
    if (isProblemChecked(problemID)) {
      const idx = currentProblems.value.findIndex(i => i === problemID)
      currentProblems.value.splice(idx, 1)
    } else {
      currentProblems.value.push(problemID)
    }
  }

  return {
    additionRef,
    currentProblems,
    isProblemChecked,
    problemTypes,
    infoOrigin,
    checkProblem,
    isSubmitDisabled,
    userUploadInfo,
  }
}

/**
 * 影视集合的滚动方式
 */
export const useScrollCollection = (containerNodeRef: Ref<HTMLDivElement | null>, containerInnerNodeRef: Ref<HTMLDivElement | null>, initScrollToActive?:boolean) => {
  /**
   * 向左滚动按钮是否展示
   */
  const isScrollLeftShow = ref(false)
  /**
   * 向右滚动按钮是否展示
   */
  const isScrollRightShow = ref(false)

  /**
   * 是否已经绑定ResizeObserver
   */
  const isObserving = ref(false)

  /**
   * 页码
   */

  const pageIndex = ref(1)

  /**
   * 页数
   */

  const pageTotal = ref(1)

  /**
   * 监听容器大小变化的observer
   */
  const observer = new ResizeObserver(() => {
    if (containerInnerNodeRef.value && containerNodeRef.value) {
      const containerClientWidth = containerNodeRef.value.clientWidth
      const containerInneClientWidth = containerInnerNodeRef.value.clientWidth
      if (containerClientWidth >= containerInneClientWidth) {
        isScrollRightShow.value = false
      } else {
        isScrollRightShow.value = true
      }
      pageTotal.value = Math.ceil(containerInneClientWidth / containerClientWidth)
    }
  })
  /**
   * 向右滚动
   * 将当前展示的最后一个滚动到最前面
   */

  const scrollRight = () => {
    if (containerNodeRef.value && containerInnerNodeRef.value?.firstElementChild) {
      const containerWidth = containerNodeRef.value.clientWidth
      // const containerScrollLeft = containerNodeRef.value.scrollLeft
      // const cardWidth = containerInnerNodeRef.value.firstElementChild.clientWidth
      // 当前展示的最后一张卡片的前一张。最后一张可能展示不完全
      // const currentLastCompleteShowingCard = Math.floor((containerWidth + containerScrollLeft) / cardWidth) 
      containerNodeRef.value.scrollTo({
        // left: currentLastCompleteShowingCard * cardWidth, // 超出会自动停止，无需处理。
        left: pageIndex.value * containerWidth,
        behavior: 'smooth',
      })
      // pageIndex.value = Math.min(pageIndex.value + 1, pageTotal.value)
    }
  }
  /**
   * 向左滚动
   * 将当前展示的第一个滚动到最后面
   */
  const scrollLeft = () => {
    if (containerNodeRef.value && containerInnerNodeRef.value?.firstElementChild) {
      const containerWidth = containerNodeRef.value.clientWidth
      const containerScrollLeft = containerNodeRef.value.scrollLeft
      // const cardWidth = containerInnerNodeRef.value.firstElementChild.clientWidth
      // 当前展示的最后一张卡片的前一张。最后一张可能展示不完全
      // const currentFirstShowingCard = Math.ceil(containerScrollLeft / cardWidth) 
      containerNodeRef.value.scrollTo({
        // left: (currentFirstShowingCard * cardWidth) - containerWidth, // 超出会自动停止，无需处理。
        left: containerScrollLeft - containerWidth,
        behavior: 'smooth',
      })
      // pageIndex.value = Math.max(pageIndex.value - 1, 1)
    }
  }

  /*
  * 需要滚动到激活项
  */ 
  const scrollToActiveItem = () => {
    console.log('call scrollToActiveItem', containerNodeRef.value, containerInnerNodeRef.value?.firstElementChild)
    if (containerNodeRef.value && containerInnerNodeRef.value?.firstElementChild) {
      const containerWidth = containerNodeRef.value.clientWidth
      const containerScrollLeft = containerNodeRef.value.scrollLeft
      const activeItem = containerInnerNodeRef.value.querySelector('.active')
      console.log('scrollToActiveItem', activeItem)
      if (activeItem) {
        const cardWidth = activeItem.clientWidth
        // 当前展示的最后一张卡片的前一张。最后一张可能展示不完全
        const currentFirstShowingCard = Math.ceil(containerScrollLeft / cardWidth)
        containerNodeRef.value.scrollTo({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          left: activeItem.offsetLeft || (currentFirstShowingCard * cardWidth) - containerWidth, // 超出会自动停止，无需处理。
          // behavior: 'smooth',
        })
      }
    }
  }

  /**
   * 监听滚动事件，判断是否滚动到两端，控制向左滚动和向右滚动按钮的展示
   */
  const onScroll = throttle(() => {
    if (containerNodeRef.value && containerInnerNodeRef.value) {
      if (containerNodeRef.value.scrollLeft === 0) {
        isScrollLeftShow.value = false
      } else if (!isScrollLeftShow.value) {
        isScrollLeftShow.value = true
      }
      const containerClientWidth = containerNodeRef.value.clientWidth
      const containerClientScrollLeft = containerNodeRef.value.scrollLeft
      const diffRight = Math.abs(containerNodeRef.value.scrollLeft - (containerInnerNodeRef.value.clientWidth - containerNodeRef.value.clientWidth))

      if (diffRight < 15) {
        isScrollRightShow.value = false
        pageIndex.value = pageTotal.value
      } else if (!isScrollRightShow.value) {
        isScrollRightShow.value = true
        pageIndex.value = Math.ceil(containerClientScrollLeft / containerClientWidth) + 1
      } else {
        pageIndex.value = Math.ceil(containerClientScrollLeft / containerClientWidth) + 1
      }
    }
  }, 16)

  const observe = () => {
    if (containerNodeRef.value && containerInnerNodeRef.value && !isObserving.value) {
      containerNodeRef.value.addEventListener('scroll', onScroll)
      observer.observe(containerNodeRef.value)
      observer.observe(containerInnerNodeRef.value)
      isObserving.value = true
    }
  }

  const unobserve = () => {
    if (containerNodeRef.value && containerInnerNodeRef.value && isObserving.value) {
      containerNodeRef.value.removeEventListener('scroll', onScroll)
      observer.unobserve(containerNodeRef.value)
      observer.unobserve(containerInnerNodeRef.value)
      isObserving.value = false
    }
  }

  /**
   * 添加滚动监听事件和ResizeObserver
   */
  onMounted(() => {
    observe()
    if (initScrollToActive) scrollToActiveItem()
  })

  /**
   * 移除滚动监听事件和ResizeObserver
   */
  onBeforeUnmount(() => {
    unobserve()
  })

  return {
    isScrollLeftShow,
    isScrollRightShow,
    scrollLeft,
    scrollRight,
    scrollToActiveItem,
    observe,
    unobserve,
    pageIndex,
    pageTotal,
  }
}